interface ArtDecoFrameProps {
  className?: string;
  /** "outside" = corners at edges of a padded parent; "inside" = corners inset within the image */
  mode?: "outside" | "inside";
  /** Color theme for the ornaments. Defaults to "gold". */
  color?: "gold" | "white";
}

/**
 * Art Deco 4-corner frame ornaments with 3 graduated L-shaped lines.
 * Ornaments grow slightly on hover of the parent (group).
 *
 * - mode="outside" (default): parent needs `relative p-5`, corners sit at container edges.
 * - mode="inside": parent needs `relative`, corners sit inside with inset offset.
 * - color="gold" (default) | "white": sets the ornament color.
 */
export default function ArtDecoFrame({ className = "", mode = "outside", color = "gold" }: ArtDecoFrameProps) {
  const inset = mode === "inside" ? "top-2.5 left-2.5" : "top-0 left-0";
  const insetTR = mode === "inside" ? "top-2.5 right-2.5" : "top-0 right-0";
  const insetBL = mode === "inside" ? "bottom-2.5 left-2.5" : "bottom-0 left-0";
  const insetBR = mode === "inside" ? "bottom-2.5 right-2.5" : "bottom-0 right-0";

  const c1 = color === "white" ? "bg-white/80 group-hover:bg-white/95" : "bg-gold/80 group-hover:bg-gold/95";
  const c2 = color === "white" ? "bg-white/55 group-hover:bg-white/75" : "bg-gold/55 group-hover:bg-gold/75";
  const c3 = color === "white" ? "bg-white/35 group-hover:bg-white/55" : "bg-gold/35 group-hover:bg-gold/55";

  const transition = "transition-all duration-500 ease-out";

  return (
    <>
      {/* Top-left */}
      <div
        className={`absolute ${inset} w-6 h-6 sm:w-11 sm:h-11 pointer-events-none z-10 ${className}`}
        aria-hidden="true"
      >
        <span className={`absolute top-0 left-0 w-6 sm:w-11 group-hover:w-8 sm:group-hover:w-14 h-px ${c1} ${transition}`} />
        <span className={`absolute top-[3px] sm:top-[4px] left-0 w-4 sm:w-8 group-hover:w-5 sm:group-hover:w-10 h-px ${c2} ${transition}`} />
        <span className={`absolute top-[6px] sm:top-[8px] left-0 w-2.5 sm:w-5 group-hover:w-3.5 sm:group-hover:w-7 h-px ${c3} ${transition}`} />
        <span className={`absolute top-0 left-0 h-6 sm:h-11 group-hover:h-8 sm:group-hover:h-14 w-px ${c1} ${transition}`} />
        <span className={`absolute top-0 left-[3px] sm:left-[4px] h-4 sm:h-8 group-hover:h-5 sm:group-hover:h-10 w-px ${c2} ${transition}`} />
        <span className={`absolute top-0 left-[6px] sm:left-[8px] h-2.5 sm:h-5 group-hover:h-3.5 sm:group-hover:h-7 w-px ${c3} ${transition}`} />
      </div>

      {/* Top-right */}
      <div
        className={`absolute ${insetTR} w-6 h-6 sm:w-11 sm:h-11 pointer-events-none z-10 ${className}`}
        aria-hidden="true"
      >
        <span className={`absolute top-0 right-0 w-6 sm:w-11 group-hover:w-8 sm:group-hover:w-14 h-px ${c1} ${transition}`} />
        <span className={`absolute top-[3px] sm:top-[4px] right-0 w-4 sm:w-8 group-hover:w-5 sm:group-hover:w-10 h-px ${c2} ${transition}`} />
        <span className={`absolute top-[6px] sm:top-[8px] right-0 w-2.5 sm:w-5 group-hover:w-3.5 sm:group-hover:w-7 h-px ${c3} ${transition}`} />
        <span className={`absolute top-0 right-0 h-6 sm:h-11 group-hover:h-8 sm:group-hover:h-14 w-px ${c1} ${transition}`} />
        <span className={`absolute top-0 right-[3px] sm:right-[4px] h-4 sm:h-8 group-hover:h-5 sm:group-hover:h-10 w-px ${c2} ${transition}`} />
        <span className={`absolute top-0 right-[6px] sm:right-[8px] h-2.5 sm:h-5 group-hover:h-3.5 sm:group-hover:h-7 w-px ${c3} ${transition}`} />
      </div>

      {/* Bottom-left */}
      <div
        className={`absolute ${insetBL} w-6 h-6 sm:w-11 sm:h-11 pointer-events-none z-10 ${className}`}
        aria-hidden="true"
      >
        <span className={`absolute bottom-0 left-0 w-6 sm:w-11 group-hover:w-8 sm:group-hover:w-14 h-px ${c1} ${transition}`} />
        <span className={`absolute bottom-[3px] sm:bottom-[4px] left-0 w-4 sm:w-8 group-hover:w-5 sm:group-hover:w-10 h-px ${c2} ${transition}`} />
        <span className={`absolute bottom-[6px] sm:bottom-[8px] left-0 w-2.5 sm:w-5 group-hover:w-3.5 sm:group-hover:w-7 h-px ${c3} ${transition}`} />
        <span className={`absolute bottom-0 left-0 h-6 sm:h-11 group-hover:h-8 sm:group-hover:h-14 w-px ${c1} ${transition}`} />
        <span className={`absolute bottom-0 left-[3px] sm:left-[4px] h-4 sm:h-8 group-hover:h-5 sm:group-hover:h-10 w-px ${c2} ${transition}`} />
        <span className={`absolute bottom-0 left-[6px] sm:left-[8px] h-2.5 sm:h-5 group-hover:h-3.5 sm:group-hover:h-7 w-px ${c3} ${transition}`} />
      </div>

      {/* Bottom-right */}
      <div
        className={`absolute ${insetBR} w-6 h-6 sm:w-11 sm:h-11 pointer-events-none z-10 ${className}`}
        aria-hidden="true"
      >
        <span className={`absolute bottom-0 right-0 w-6 sm:w-11 group-hover:w-8 sm:group-hover:w-14 h-px ${c1} ${transition}`} />
        <span className={`absolute bottom-[3px] sm:bottom-[4px] right-0 w-4 sm:w-8 group-hover:w-5 sm:group-hover:w-10 h-px ${c2} ${transition}`} />
        <span className={`absolute bottom-[6px] sm:bottom-[8px] right-0 w-2.5 sm:w-5 group-hover:w-3.5 sm:group-hover:w-7 h-px ${c3} ${transition}`} />
        <span className={`absolute bottom-0 right-0 h-6 sm:h-11 group-hover:h-8 sm:group-hover:h-14 w-px ${c1} ${transition}`} />
        <span className={`absolute bottom-0 right-[3px] sm:right-[4px] h-4 sm:h-8 group-hover:h-5 sm:group-hover:h-10 w-px ${c2} ${transition}`} />
        <span className={`absolute bottom-0 right-[6px] sm:right-[8px] h-2.5 sm:h-5 group-hover:h-3.5 sm:group-hover:h-7 w-px ${c3} ${transition}`} />
      </div>
    </>
  );
}
